import {
  fetchWidgetConfig,
  mintPublicSession,
  newExternalId,
  PublicMintError,
  readIdentity,
  writeIdentity,
  type PublicIdentity,
} from './identity';
import { decodeJwtClaims } from './jwt';
import { buildMetainfo, type WidgetMetainfo } from './metainfo';
import { DungoTransport } from './transport';
import { WidgetUI, type PrechatFormData } from './ui';
import {
  ChatResueltoException,
  SesionDesactualizadaException,
  isStateEvent,
  type DungoWidgetConfig,
  type DungoWidgetInstance,
  type DungoWidgetSession,
  type HistoryMessage,
  type WsMessageEvent,
  type WsStateEvent,
  type WsTopicEvent,
} from './types';

const NOTICE_RESUELTA = 'La conversación finalizó.';
const NOTICE_START_ERROR = 'No se pudo iniciar la conversación. Inténtalo de nuevo.';
const NOTICE_DOMINIO_NO_PERMITIDO = 'Este chat no está disponible en este sitio.';
const NOTICE_MINT_DESHABILITADO = 'El inicio de chat no está habilitado en este sitio.';
const NOTICE_IMAGE_ERROR = 'No se pudo enviar la imagen. Inténtalo de nuevo.';
const NOTICE_DOC_ERROR = 'No se pudo enviar el documento. Inténtalo de nuevo.';

/** Placeholders que el backend guarda como `mensaje` cuando el media no trae caption. */
const IMAGE_PLACEHOLDER = '[imagen]';
const DOC_PLACEHOLDER = '[documento]';

export class DungoWidget implements DungoWidgetInstance {
  private readonly ui: WidgetUI;
  private readonly transport: DungoTransport;
  private readonly cfg: DungoWidgetConfig;
  private destroyed = false;
  private historyLoaded = false;
  /**
   * MODO PÚBLICO activo: hay `apiKey` + `backendBaseUrl` y NO hay `token` ni
   * `getSession` (el modo embebido tiene prioridad si el host pasa ambos).
   */
  private readonly publicFlow: boolean;
  /** Identidad del visitante en modo público (persistida en localStorage). */
  private identity: PublicIdentity | null = null;
  /** Claves de mensajes ya pintados (historial) para deduplicar el eco del realtime. */
  private readonly seen = new Set<string>();
  /** AudioContext para el aviso sonoro; se crea/desbloquea en el gesto de "Iniciar". */
  private audioCtx: AudioContext | null = null;

  constructor(config: DungoWidgetConfig) {
    if (!config?.reciverBaseUrl) throw new Error('[DungoWidget] falta `reciverBaseUrl`.');
    if (!config?.wsUrl) throw new Error('[DungoWidget] falta `wsUrl`.');
    if (config?.apiKey && !config?.backendBaseUrl) {
      throw new Error('[DungoWidget] el modo público requiere `backendBaseUrl` junto con `apiKey`.');
    }
    const hasPublic = !!(config?.apiKey && config?.backendBaseUrl);
    if (!config?.token && !config?.getSession && !hasPublic) {
      throw new Error(
        '[DungoWidget] falta `token`/`getSession` (modo embebido) o `apiKey`+`backendBaseUrl` (modo público).',
      );
    }
    this.cfg = config;
    this.publicFlow = hasPublic && !config.token && !config.getSession;

    this.ui = new WidgetUI(
      {
        onSend: (text) => this.handleSend(text),
        onSendImage: (file, caption) => void this.handleSendImage(file, caption),
        onToggle: () => {
          /* idle al abrir: no conectamos ni minteamos hasta "Iniciar Conversación". */
        },
        onStart: () => void this.handleStart(),
        onPrechatSubmit: (data) => this.handlePrechatSubmit(data),
        onChangeData: () => this.handleChangeData(),
      },
      config.theme ?? {},
      { defaultCountry: config.defaultCountry },
    );

    // Modo público: si el visitante ya se identificó antes en este navegador se
    // salta el formulario (con link "Cambiar mis datos"); si no, formulario.
    if (this.publicFlow) {
      this.identity = readIdentity(config.apiKey!);
      if (this.identity) this.ui.showIdleStart(true);
      else this.ui.showPrechat();
      // Color del channel en runtime, salvo que el host ya haya fijado el suyo.
      if (!config.theme?.primaryColor) void this.applyRemoteTheme();
    }

    this.transport = new DungoTransport(
      {
        wsUrl: config.wsUrl,
        reciverBaseUrl: config.reciverBaseUrl,
      },
      {
        onTopicEvent: (e) => this.handleTopicEvent(e),
        onState: (s) => this.ui.setState(s),
      },
    );
  }

  /**
   * Obtiene la sesión a usar para iniciar una conversación:
   *   - `getSession()` si está (prioritario; mintea bajo demanda en el host).
   *   - el `token` estático de la config como fallback.
   *   - modo público: mint directo con la api_key + identidad del formulario.
   */
  private async resolveSession(): Promise<DungoWidgetSession> {
    if (this.cfg.getSession) {
      return this.cfg.getSession();
    }
    if (this.cfg.token) {
      return { token: this.cfg.token, chatId: this.cfg.chatId };
    }
    if (this.publicFlow) {
      const identity = this.identity ?? readIdentity(this.cfg.apiKey!);
      if (!identity) {
        this.ui.showPrechat();
        throw new Error('[DungoWidget] falta la identidad del visitante (formulario de pre-chat).');
      }
      this.identity = identity;
      return mintPublicSession(this.cfg.backendBaseUrl!, this.cfg.apiKey!, identity);
    }
    throw new Error('[DungoWidget] no hay forma de obtener una sesión (`token`/`getSession`).');
  }

  /**
   * MODO PÚBLICO: el visitante envió el formulario. Conserva el `externalId` si
   * ya existía (edición de datos ⇒ mismo contacto), persiste la identidad y
   * arranca la conversación. Si el mint falla, el formulario queda para reintentar.
   */
  private handlePrechatSubmit(data: PrechatFormData): void {
    if (this.destroyed || !this.publicFlow) return;
    this.identity = {
      externalId: this.identity?.externalId ?? newExternalId(),
      nombre: data.nombre,
      apellido: data.apellido,
      email: data.email,
      prefijo: data.prefijo,
      numero: data.numero,
    };
    writeIdentity(this.cfg.apiKey!, this.identity);
    this.ui.setPrechatSubmitting(true);
    void this.handleStart().finally(() => {
      if (this.destroyed) return;
      this.ui.setPrechatSubmitting(false);
      // Si conectó, la próxima vista idle (chat resuelto) será el botón + link.
      if (this.ui.getMode() === 'active') this.ui.showIdleStart(true);
    });
  }

  /** MODO PÚBLICO: re-muestra el formulario prellenado con los datos guardados. */
  private handleChangeData(): void {
    if (!this.publicFlow) return;
    const identity = this.identity ?? readIdentity(this.cfg.apiKey!);
    this.ui.showPrechat(identity ?? undefined);
  }

  /**
   * MODO PÚBLICO: aplica el color del widget configurado en el channel
   * (GET /api/web/widget-config). Best-effort y solo si el host no fijó
   * `theme.primaryColor`; ante cualquier error se queda el default.
   */
  private async applyRemoteTheme(): Promise<void> {
    const config = await fetchWidgetConfig(this.cfg.backendBaseUrl!, this.cfg.apiKey!);
    if (this.destroyed || !config?.widgetColor) return;
    if (!/^#[0-9a-fA-F]{6}$/.test(config.widgetColor)) return;
    this.ui.setPrimaryColor(config.widgetColor);
  }

  /** Mensaje de aviso según el error de inicio (códigos del mint público incluidos). */
  private startErrorNotice(err: unknown): string {
    if (err instanceof PublicMintError) {
      if (err.code === 'DOMINIO_NO_PERMITIDO') return NOTICE_DOMINIO_NO_PERMITIDO;
      if (err.code === 'MINT_PUBLICO_DESHABILITADO') return NOTICE_MINT_DESHABILITADO;
    }
    return NOTICE_START_ERROR;
  }

  /**
   * Flujo "Iniciar Conversación": obtiene sesión, resuelve tenant/chatId,
   * configura el transporte, conecta STOMP, rehidrata historial y pasa a active.
   * Maneja errores con gracia: deja el botón para reintentar.
   */
  private async handleStart(): Promise<void> {
    if (this.destroyed) return;
    // "Iniciar Conversación" es un gesto del usuario: aprovechamos para desbloquear
    // el audio (autoplay policy) antes de que lleguen respuestas del agente.
    this.unlockAudio();
    this.ui.setStarting(true);
    this.ui.clearNotice();
    try {
      const session = await this.resolveSession();
      if (this.destroyed) return;

      const claims = decodeJwtClaims(session.token);
      const tenant = this.cfg.tenant ?? claims.tenant;
      const chatId = session.chatId ?? this.cfg.chatId ?? claims.chat_id;
      if (!tenant || !chatId) {
        throw new Error('[DungoWidget] no se pudo resolver tenant/chatId de la sesión.');
      }

      // Conversación nueva: reseteamos render y dedup.
      this.seen.clear();
      this.historyLoaded = false;
      this.ui.clearMessages();

      this.transport.configure({ token: session.token, tenant, chatId });
      this.ui.setMode('active');
      this.transport.connect();
      await this.loadHistory();
    } catch (err) {
      if (this.destroyed) return;
      console.warn('[DungoWidget] no se pudo iniciar la conversación:', err);
      this.transport.disconnect();
      this.ui.setMode('idle');
      this.ui.showNotice(this.startErrorNotice(err));
    } finally {
      this.ui.setStarting(false);
    }
  }

  /** Da por finalizada la conversación: avisa, desconecta y vuelve a idle. */
  private goIdleResolved(): void {
    this.transport.disconnect();
    this.ui.setMode('idle');
    this.ui.showNotice(NOTICE_RESUELTA);
  }

  /** Valor legacy de sección: `getSection()` (prioritario) con fallback a `section`. */
  private resolveSectionLegacy(): string | Record<string, unknown> | undefined {
    if (this.cfg.getSection) {
      try {
        return this.cfg.getSection();
      } catch {
        /* ignora errores del callback del host */
      }
    }
    return this.cfg.section;
  }

  /** Metadatos arbitrarios del host: `getExtra()` (prioritario) con fallback a `extra`. */
  private resolveExtra(): Record<string, unknown> | undefined {
    if (this.cfg.getExtra) {
      try {
        return this.cfg.getExtra();
      } catch {
        /* ignora errores del callback del host */
      }
    }
    return this.cfg.extra;
  }

  /**
   * Arma el objeto de metainfo que viaja en `seccion` en CADA envío (texto o
   * media): campos automáticos del entorno + `seccion` legacy + `extra` del host.
   * Nunca lanza (el helper es defensivo por campo).
   */
  private resolveMetainfo(): WidgetMetainfo {
    return buildMetainfo(this.resolveSectionLegacy(), this.resolveExtra());
  }

  /**
   * Plan 25: el token reservó un chat que otra sesión del mismo visitante ya creó
   * (409 SESION_DESACTUALIZADA). Se re-inicia la conversación (mint nuevo ⇒ el chat
   * existente y su historial) y se devuelve true si quedó activa para reenviar.
   */
  private async renovarSesion(): Promise<boolean> {
    await this.handleStart();
    return !this.destroyed && this.ui.getMode() === 'active';
  }

  private async handleSend(text: string, reintento = false): Promise<void> {
    const node = this.ui.addMessage({
      text,
      direction: 'out',
      timestamp: new Date().toISOString(),
      status: 'sending',
    });
    this.ui.setSending(true);
    try {
      await this.transport.send(text, this.resolveMetainfo());
    } catch (err) {
      if (err instanceof SesionDesactualizadaException && !reintento) {
        this.ui.markError(node);
        this.ui.setSending(false);
        if (await this.renovarSesion()) await this.handleSend(text, true);
        return;
      }
      if (err instanceof ChatResueltoException) {
        // 409 CHAT_RESUELTO: no reintentar; tratar igual que el evento RESUELTA.
        this.ui.markError(node);
        this.goIdleResolved();
        return;
      }
      this.ui.markError(node);
    } finally {
      this.ui.setSending(false);
    }
  }

  /**
   * Envío de media (imagen o documento): pinta el eco propio optimista (con
   * preview local), sube por `POST /native/media` y, al resolverse, registra la
   * clave para dedup. Maneja 409 igual que el flujo de texto.
   */
  private async handleSendImage(file: File, caption: string, reintento = false): Promise<void> {
    const isDoc = !(file.type || '').toLowerCase().startsWith('image/');
    const localUrl = URL.createObjectURL(file);
    const cap = this.normalizeCaption(caption);
    const node = this.ui.addMessage({
      text: cap ?? '',
      direction: 'out',
      timestamp: new Date().toISOString(),
      status: 'sending',
      ...(isDoc
        ? { docUrl: localUrl, docName: file.name, docMime: file.type }
        : { imageUrl: localUrl }),
      caption: cap,
    });
    this.ui.setSending(true);
    try {
      const res = await this.transport.sendMedia(file, cap, this.resolveMetainfo());
      // Registramos la clave para que el eventual eco por STOMP no duplique.
      const key = this.msgKey('out', res.conversacionId, res.mensaje, res.fechaHoraMsj);
      this.seen.add(key);
    } catch (err) {
      if (err instanceof SesionDesactualizadaException && !reintento) {
        this.ui.markError(node);
        this.ui.setSending(false);
        if (await this.renovarSesion()) await this.handleSendImage(file, caption, true);
        return;
      }
      if (err instanceof ChatResueltoException) {
        this.ui.markError(node);
        this.goIdleResolved();
        return;
      }
      console.warn('[DungoWidget] no se pudo enviar el media:', err);
      this.ui.markError(node);
      this.ui.showNotice(isDoc ? NOTICE_DOC_ERROR : NOTICE_IMAGE_ERROR);
    } finally {
      this.ui.setSending(false);
      // El preview local ya está pintado en el <img>; el object URL debe vivir
      // hasta que el navegador haya decodificado la imagen. Lo revocamos en el
      // próximo tick para no romper el render.
      setTimeout(() => URL.revokeObjectURL(localUrl), 0);
    }
  }

  /**
   * Normaliza el caption/`mensaje` para el render: el backend usa `[imagen]` como
   * placeholder cuando no hay caption; en ese caso no mostramos texto.
   */
  private normalizeCaption(mensaje: string | null | undefined): string | undefined {
    if (!mensaje) return undefined;
    const trimmed = mensaje.trim();
    if (!trimmed || trimmed === IMAGE_PLACEHOLDER || trimmed === DOC_PLACEHOLDER) return undefined;
    return mensaje;
  }

  /** ¿El evento/ítem es una imagen renderizable (tiene tipoMensaje IMAGEN + mediaUrl)? */
  private isImage(tipoMensaje?: string | null, mediaUrl?: string | null): mediaUrl is string {
    return tipoMensaje === 'IMAGEN' && !!mediaUrl;
  }

  /** ¿El evento/ítem es un documento renderizable (tiene tipoMensaje DOCUMENTO + mediaUrl)? */
  private isDocument(tipoMensaje?: string | null, mediaUrl?: string | null): mediaUrl is string {
    return tipoMensaje === 'DOCUMENTO' && !!mediaUrl;
  }

  /**
   * Clave de dedup estable entre historial y realtime. No hay id de mensaje, así
   * que combinamos dirección + conversación + texto + timestamp.
   */
  private msgKey(direction: 'in' | 'out', conversacionId: string, mensaje: string, ts: string): string {
    return `${direction}|${conversacionId}|${ts}|${mensaje}`;
  }

  /** Resuelve la dirección (mismo criterio que el realtime y el historial). */
  private directionOf(agenteIdDun: string | null, tipoAgenteIdDun: string | null): 'in' | 'out' {
    const fromContact = agenteIdDun == null && tipoAgenteIdDun == null;
    return fromContact ? 'out' : 'in';
  }

  /**
   * Punto de entrada de todo lo que llega por el topic. Discrimina por
   * `tipoEvento`: los eventos de ESTADO NO se pintan como mensajes.
   */
  private handleTopicEvent(e: WsTopicEvent): void {
    if (isStateEvent(e)) {
      this.handleStateEvent(e);
      return;
    }
    this.handleIncoming(e);
  }

  private handleStateEvent(e: WsStateEvent): void {
    if (e.estado === 'RESUELTA') {
      this.goIdleResolved();
    }
  }

  private handleIncoming(e: WsMessageEvent): void {
    const direction = this.directionOf(e.agenteIdDun, e.tipoAgenteIdDun);

    // El topic emite también el eco del propio contacto (ambos ids null).
    // Como ya lo pintamos optimista al enviar, ignoramos ese eco para no duplicar.
    if (direction === 'out') return;

    // Dedup contra lo ya pintado por el historial (rehidratación al abrir).
    const key = this.msgKey(direction, e.conversacionIdDun, e.mensaje, e.fechaHoraMsj);
    if (this.seen.has(key)) return;
    this.seen.add(key);

    // Audio u otros tipos no soportados: se ignoran con gracia (no se pintan).
    if (e.tipoMensaje && e.tipoMensaje !== 'IMAGEN' && e.tipoMensaje !== 'DOCUMENTO') return;

    // Respuesta real del agente que sí se va a mostrar: avisamos con sonido
    // (si el contacto no lo silenció). Útil cuando está en otra pestaña/tarea.
    this.playChime();

    if (this.isImage(e.tipoMensaje, e.mediaUrl)) {
      const cap = this.normalizeCaption(e.mensaje);
      this.ui.addMessage({
        text: cap ?? '',
        direction: 'in',
        name: e.name,
        timestamp: e.fechaHoraMsj,
        imageUrl: e.mediaUrl,
        caption: cap,
      });
      return;
    }

    if (this.isDocument(e.tipoMensaje, e.mediaUrl)) {
      const cap = this.normalizeCaption(e.mensaje);
      this.ui.addMessage({
        text: cap ?? '',
        direction: 'in',
        name: e.name,
        timestamp: e.fechaHoraMsj,
        docUrl: e.mediaUrl,
        docName: e.mediaFilename ?? undefined,
        docMime: e.mimeType ?? undefined,
        caption: cap,
      });
      return;
    }

    this.ui.addMessage({
      text: e.mensaje,
      direction: 'in',
      name: e.name,
      timestamp: e.fechaHoraMsj,
    });
  }

  /** Crea (una vez) y reanuda el AudioContext. Debe llamarse dentro de un gesto del usuario. */
  private unlockAudio(): void {
    try {
      if (!this.audioCtx) {
        const Ctx: typeof AudioContext | undefined =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) return;
        this.audioCtx = new Ctx();
      }
      if (this.audioCtx.state === 'suspended') void this.audioCtx.resume();
    } catch {
      /* audio no disponible en este navegador: seguimos sin sonido */
    }
  }

  /**
   * Aviso sonoro al llegar una respuesta del agente: dos notas cortas ascendentes
   * generadas con Web Audio (sin assets). No suena si el contacto lo silenció o si
   * el audio no pudo desbloquearse.
   */
  private playChime(): void {
    if (!this.ui.isSoundEnabled()) return;
    const ctx = this.audioCtx;
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') void ctx.resume();
      const now = ctx.currentTime;
      const notes: Array<{ freq: number; at: number }> = [
        { freq: 660, at: 0 },
        { freq: 880, at: 0.12 },
      ];
      for (const n of notes) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = n.freq;
        const start = now + n.at;
        const end = start + 0.18;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.2, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, end);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(end + 0.02);
      }
    } catch {
      /* si el audio falla, nunca interrumpimos el chat */
    }
  }

  /**
   * Rehidrata el historial al iniciar la conversación.
   * Los mensajes llegan en orden ascendente y se pintan antes que los nuevos.
   * Registra cada uno en `seen` para que el eventual eco por STOMP no se duplique.
   */
  private async loadHistory(): Promise<void> {
    if (this.historyLoaded || this.destroyed) return;
    this.historyLoaded = true;

    const items = await this.transport.fetchHistory();
    if (this.destroyed) return;

    for (const item of items) {
      this.renderHistoryItem(item);
    }
  }

  private renderHistoryItem(item: HistoryMessage): void {
    const direction = this.directionOf(item.agenteIdDun, item.tipoAgenteIdDun);
    const key = this.msgKey(direction, item.conversacionId, item.mensaje, item.fechaHoraMsj);
    if (this.seen.has(key)) return;
    this.seen.add(key);

    // Audio u otros tipos no soportados: se omiten del historial con gracia.
    if (item.tipoMensaje && item.tipoMensaje !== 'IMAGEN' && item.tipoMensaje !== 'DOCUMENTO') return;

    if (this.isImage(item.tipoMensaje, item.mediaUrl)) {
      const cap = this.normalizeCaption(item.mensaje);
      this.ui.addMessage({
        text: cap ?? '',
        direction,
        name: direction === 'in' ? item.name : undefined,
        timestamp: item.fechaHoraMsj,
        imageUrl: item.mediaUrl,
        caption: cap,
      });
      return;
    }

    if (this.isDocument(item.tipoMensaje, item.mediaUrl)) {
      const cap = this.normalizeCaption(item.mensaje);
      this.ui.addMessage({
        text: cap ?? '',
        direction,
        name: direction === 'in' ? item.name : undefined,
        timestamp: item.fechaHoraMsj,
        docUrl: item.mediaUrl,
        docName: item.mediaFilename ?? undefined,
        docMime: item.mimeType ?? undefined,
        caption: cap,
      });
      return;
    }

    this.ui.addMessage({
      text: item.mensaje,
      direction,
      name: direction === 'in' ? item.name : undefined,
      timestamp: item.fechaHoraMsj,
    });
  }

  open(): void {
    this.ui.toggle(true);
  }
  close(): void {
    this.ui.toggle(false);
  }
  toggle(): void {
    this.ui.toggle();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.transport.disconnect();
    this.ui.destroy();
  }
}
